import {wordpressApi, WordPressMedia, WordPressPost} from "../../services/wordpressApi";
import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";

interface NewsCardProps {
    post: WordPressPost;
    variant?: 'default' | 'featured' | 'compact';
}

const NewsCard: React.FC<NewsCardProps> = ({ post, variant = 'default' }) => {
    const [featuredImage, setFeaturedImage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadFeaturedImage = async () => {
            if (post.featured_media && post.featured_media > 0) {
                setLoading(true);
                try {
                    const media: WordPressMedia = await wordpressApi.getMedia(post.featured_media);
                    setFeaturedImage(media.source_url);
                } catch (error) {
                    console.error('Erreur lors du chargement de l\'image:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadFeaturedImage();
    }, [post.featured_media]);

    const getCardClasses = () => {
        const baseClasses = "bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105";

        switch (variant) {
            case 'featured':
                return `${baseClasses} md:flex md:flex-row`;
            case 'compact':
                return `${baseClasses} max-w-sm`;
            default:
                return baseClasses;
        }
    };

    const getImageClasses = () => {
        switch (variant) {
            case 'featured':
                return "md:w-1/2 h-64 md:h-auto";
            case 'compact':
                return "h-48";
            default:
                return "h-56";
        }
    };

    const getContentClasses = () => {
        switch (variant) {
            case 'featured':
                return "md:w-1/2 p-6";
            default:
                return "p-6";
        }
    };

    return (
        <article className={getCardClasses()}>
            {/* Image */}
            {featuredImage && (
                <Link to={`/actualites/${post.slug}`} className={`${getImageClasses()} relative overflow-hidden block`}>
                    {loading ? (
                        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                            <span className="text-gray-400">Chargement...</span>
                        </div>
                    ) : (
                        <img
                            src={featuredImage}
                            alt={wordpressApi.stripHtml(post.title.rendered)}
                            className="w-full h-full object-cover transition-transform hover:scale-110"
                            loading="lazy"
                        />
                    )}
                </Link>
            )}

            {/* Contenu */}
            <div className={getContentClasses()}>
                {/* Date */}
                <time className="text-sm text-gray-500 block mb-2">
                    {wordpressApi.formatDate(post.date)}
                </time>

                {/* Titre */}
                <h3 className={`font-bold text-gray-900 mb-3 ${
                    variant === 'featured' ? 'text-2xl' : 'text-xl'
                }`}>
                    <Link
                        to={`/actualites/${post.slug}`}
                        className="hover:text-blue-600 transition-colors"
                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                    />
                </h3>

                {/* Extrait */}
                <div
                    className="text-gray-600 mb-4 line-clamp-3"
                    dangerouslySetInnerHTML={{
                        __html: post.excerpt.rendered || wordpressApi.stripHtml(post.content.rendered).substring(0, 150) + '...'
                    }}
                />

                {/* Lien de lecture */}
                <Link
                    to={`/actualites/${post.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                    Lire la suite
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </Link>
            </div>
        </article>
    );
};

export  { NewsCard } ;
